/**
 * ProfilePage
 * Main page for managing personal and professional profiles
 * Auth sync handled by ProtectedRoute, profile data fetched via React Query
 */

import { Alert, AlertDescription } from '@shared/components/ui/alert'
import { Button } from '@shared/components/ui/button'
import { Card } from '@shared/components/ui/card'
import { useAuth, useLoading } from '@shared/contexts'
import { useProfileTour } from '@shared/hooks'
import { actionStorage } from '@shared/lib/storage'
import { AlertCircle, Loader2, LogOut } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import {
  PersonalProfileForm,
  ProfessionalProfileForm,
  ProfileSwitcher,
  type ProfileType,
} from '../components'
import { useProfessionalProfileByUserId } from '../hooks/useProfessionalProfile'
import { useUserProfile } from '../hooks/useUserProfile'
import { logger } from '@shared/lib/utils/logger'

// ============================================================================
// Component
// ============================================================================

export function ProfilePage() {
  const navigate = useNavigate()
  const { user: authUser, logout, refetchUser } = useAuth()
  const { hideLoading } = useLoading()

  // Auto-start tour for first-time visitors
  useProfileTour(true)

  const { data: userProfile, isLoading: isLoadingUser } = useUserProfile()
  const { data: professionalProfile, isLoading: isLoadingProfessional } =
    useProfessionalProfileByUserId(authUser?.id || '')

  const [activeProfile, setActiveProfile] = useState<ProfileType>('personal')

  // Note: No need to sync here - ProtectedRoute already syncs auth when entering,
  // and useUserProfile/useProfessionalProfile fetch fresh data with React Query caching

  // Handle logout
  const handleLogout = async () => {
    try {
      // Logout (which calls the API and clears state)
      await logout()
      toast.success('Sesión cerrada correctamente')
      // Use navigate instead of window.location for better control
      void navigate('/', { replace: true })
    } catch (error) {
      logger.error('Logout failed:', error)
      toast.error('Error al cerrar sesión')
      // Even on error, navigate away and clear
      window.location.href = '/'
    }
  }

  // Handle successful profile update
  const handleSuccess = async () => {
    try {
      // Refetch user data to sync with server
      await refetchUser()

      // Stay on profile page - no navigation
    } catch (error) {
      logger.error('Failed to refetch user:', error)
    } finally {
      // Always hide loading overlay
      hideLoading()
    }
  }

  // Handle cancel
  const handleCancel = async () => {
    const pendingAction = actionStorage.getPendingAction()
    if (pendingAction?.returnPath) {
      await navigate(pendingAction.returnPath)
    } else {
      await navigate('/')
    }
  }

  // Loading state
  if (isLoadingUser || isLoadingProfessional) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-primary" />
            <p className="text-muted-foreground">Cargando perfil...</p>
          </div>
        </div>
      </div>
    )
  }

  // User data not available
  if (!userProfile || !authUser) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card className="p-6">
          <p className="text-muted-foreground">
            No se pudo cargar la información del perfil.
          </p>
          <Button onClick={() => void navigate('/')} className="mt-4">
            Volver al inicio
          </Button>
        </Card>
      </div>
    )
  }

  // Check if validation is needed based on auth context and pending actions
  const hasPendingAction = actionStorage.hasPendingAction()
  const needsValidation = hasPendingAction && !authUser?.isAccountValidated

  return (
    <div className="container mx-auto px-4 py-8 w-full">
      {/* Validation Alert */}
      {needsValidation && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Tu cuenta necesita validación para continuar con la acción solicitada.
            Por favor, valida tu cuenta.
          </AlertDescription>
        </Alert>
      )}

      {/* Profile Switcher */}
      <ProfileSwitcher
        activeProfile={activeProfile}
        onSwitch={setActiveProfile}
        isAccountValidated={userProfile?.isAccountValidated}
        className=""
        data-tour="profile-tabs"
      />

      {/* Profile Forms */}
      <Card className="p-6">
        {activeProfile === 'personal' ? (
          <div data-tour="personal-profile">
            <PersonalProfileForm
              user={userProfile}
              onSuccess={() => void handleSuccess()}
              onCancel={() => void handleCancel()}
            />
          </div>
        ) : (
          <div data-tour="professional-profile">
            <ProfessionalProfileForm
              professional={professionalProfile}
              user={userProfile}
              onSuccess={() => void handleSuccess()}
              onCancel={() => void handleCancel()}
              activeProfile={activeProfile}
            />
          </div>
        )}
      </Card>

      {/* Logout Button */}
      <div className="mt-6 flex justify-center">
        <Button variant="outline" onClick={() => void handleLogout()}>
          <LogOut className="h-4 w-4 mr-2" />
          Cerrar Sesión
        </Button>
      </div>
    </div>
  )
}
